import { useMemo, useRef, useEffect } from 'react';

import { isUntouchedBlankCreateRow } from './use-confirm';

import type { PlanTableData } from '../components/common/handsontable.types';

import { SearchOption } from '@/types/search';

/**
 * Hook to check if plan register page has unsaved changes
 * Compares current tableData with initial state when page loaded (not store state)
 * This ensures that if user reverts changes back to initial state, no popup is shown
 * @param tableData - Current table data from Handsontable
 * @param plantOptions - Plant options for conversion
 * @param witnessCompanyOptions - Witness company options for conversion
 * @param selectedProperty - Currently selected property
 * @param optionsLoaded - Whether options have been loaded (indicates initial data sync is complete)
 * @returns boolean indicating if there are unsaved changes
 */
export const usePlanRegisterUnsavedChanges = (
  tableData: PlanTableData,
  plantOptions: SearchOption[],
  witnessCompanyOptions: SearchOption[],
  selectedProperty: SearchOption | null,
  optionsLoaded: boolean
) => {
  // Store initial state when data is first loaded (after options are loaded)
  const initialTableDataRef = useRef<PlanTableData | null>(null);
  const initialSelectedPropertyRef = useRef<SearchOption | null>(null);
  const isInitializedRef = useRef(false);

  // Initialize initial state when options are loaded and tableData is first set
  // This ensures we capture the true initial state before any user interactions
  useEffect(() => {
    if (
      !isInitializedRef.current &&
      optionsLoaded &&
      tableData &&
      tableData.length > 0 &&
      plantOptions.length > 0 &&
      witnessCompanyOptions.length > 0
    ) {
      // Store initial state (deep copy) - this is the state right after initial load
      initialTableDataRef.current = JSON.parse(JSON.stringify(tableData));
      initialSelectedPropertyRef.current = selectedProperty;
      isInitializedRef.current = true;
    }
  }, [tableData, plantOptions, witnessCompanyOptions, selectedProperty, optionsLoaded]);

  // Reset initial state when options are unloaded (page reload or navigation)
  useEffect(() => {
    if (!optionsLoaded) {
      isInitializedRef.current = false;
      initialTableDataRef.current = null;
      initialSelectedPropertyRef.current = null;
    }
  }, [optionsLoaded]);

  const hasUnsavedChanges = useMemo(() => {
    // If not initialized yet, no changes
    if (!isInitializedRef.current || !initialTableDataRef.current) {
      return false;
    }

    // Check if selectedProperty has changed from initial
    const hasPropertyChanged =
      JSON.stringify(selectedProperty) !== JSON.stringify(initialSelectedPropertyRef.current);

    // Compare current tableData with initial tableData
    // Skip STT column (index 0) as it's just row numbers and can change when rows are added/deleted
    // Only compare actual data columns (from index 1 onwards)
    // Normalize undefined/null/empty string to ensure accurate comparison
    const normalizeValue = (value: unknown): unknown => {
      if (value === null || value === undefined || value === '') {
        return '';
      }
      return value;
    };

    const normalizeTableData = (data: PlanTableData): PlanTableData => {
      return data.map((row) => {
        return row.map(normalizeValue);
      });
    };

    const currentNormalized = normalizeTableData(tableData);
    const initialNormalized = normalizeTableData(initialTableDataRef.current);

    const currentTableDataStr = JSON.stringify(currentNormalized);
    const initialTableDataStr = JSON.stringify(initialNormalized);
    const hasTableChanges = currentTableDataStr !== initialTableDataStr;

    // Return true only if there are actual changes from initial state
    return hasTableChanges || hasPropertyChanged;
  }, [tableData, selectedProperty]);

  return hasUnsavedChanges;
};

/**
 * Hook to check if plan detail page has unsaved changes
 * Compares current tableData with originalTableData
 * @param tableData - Current table data from Handsontable
 * @param originalTableData - Original table data when page loaded
 * @returns boolean indicating if there are unsaved changes
 */
export const usePlanDetailUnsavedChanges = (
  tableData: PlanTableData,
  originalTableData: PlanTableData
) => {
  const hasUnsavedChanges = useMemo(() => {
    // If no data loaded yet, no changes
    if (!tableData || !originalTableData) {
      return false;
    }

    // Ignore untouched blank create rows so right-click add-row without edits
    // does not count as a meaningful unsaved change in detail edit view.
    const stripUntouchedBlankCreateRows = (data: PlanTableData): PlanTableData => {
      return data.filter((row) => !isUntouchedBlankCreateRow(row));
    };

    const normalizedCurrent = stripUntouchedBlankCreateRows(tableData);
    const normalizedOriginal = stripUntouchedBlankCreateRows(originalTableData);

    return JSON.stringify(normalizedCurrent) !== JSON.stringify(normalizedOriginal);
  }, [tableData, originalTableData]);

  return hasUnsavedChanges;
};
